import {
  StaffQueueControllerQueueV1Sort,
  StaffQueueControllerQueueV1View,
} from 'src/apis/backend/generated/models';

// ----------------------------------------------------------------------

export type QueueView =
  (typeof StaffQueueControllerQueueV1View)[keyof typeof StaffQueueControllerQueueV1View];

export type QueueSort =
  (typeof StaffQueueControllerQueueV1Sort)[keyof typeof StaffQueueControllerQueueV1Sort];

/**
 * The seven views, in the order the wireframe puts them.
 *
 * First drafts and edits sit next to My tasks rather than at the end, because
 * they are the split a producer picks work by: a blank page and a response to
 * comments are different jobs, and which one you have appetite for decides what
 * you pull. The ownership views come after.
 *
 * `countKey` names the field on the counts DTO rather than being derived from
 * `value`, because `needs_lead` and `needsLead` are spelled differently on the
 * wire and in the response and guessing between them is how a tab silently
 * shows zero.
 */
export const QUEUE_VIEWS: {
  value: QueueView;
  label: string;
  countKey: 'all' | 'mine' | 'firstDrafts' | 'edits' | 'unassigned' | 'blocked' | 'needsLead';
}[] = [
  { value: StaffQueueControllerQueueV1View.all, label: 'All open', countKey: 'all' },
  { value: StaffQueueControllerQueueV1View.mine, label: 'My tasks', countKey: 'mine' },
  {
    value: StaffQueueControllerQueueV1View.first_drafts,
    label: 'First drafts',
    countKey: 'firstDrafts',
  },
  { value: StaffQueueControllerQueueV1View.edits, label: 'Edits', countKey: 'edits' },
  {
    value: StaffQueueControllerQueueV1View.unassigned,
    label: 'Unassigned',
    countKey: 'unassigned',
  },
  { value: StaffQueueControllerQueueV1View.blocked, label: 'Blocked', countKey: 'blocked' },
  {
    value: StaffQueueControllerQueueV1View.needs_lead,
    label: 'Needs a lead',
    countKey: 'needsLead',
  },
];

/**
 * How the rows are ordered, and what each ordering is for.
 *
 * The descriptions matter more than the labels here: "oldest first" and "due
 * first" sound interchangeable until you know one pins overdue work and the
 * other deliberately does not, which is the difference between "what is most
 * urgent" and "what has been sitting here longest".
 */
export const QUEUE_SORTS: { value: QueueSort; label: string }[] = [
  { value: StaffQueueControllerQueueV1Sort.due, label: 'Due first, overdue pinned' },
  { value: StaffQueueControllerQueueV1Sort.created, label: 'Oldest first' },
  { value: StaffQueueControllerQueueV1Sort.send, label: 'Send date' },
  { value: StaffQueueControllerQueueV1Sort.state, label: 'Lifecycle state' },
];

export const DEFAULT_QUEUE_SORT: QueueSort = StaffQueueControllerQueueV1Sort.due;
